/**
 * Debug perception overlay — renders each guard's FOV, sound flood tiles,
 * awareness bars, and ghost / investigation markers. Toggled on by the
 * same Backquote press that shows the DOM debug overlay.
 *
 * Owns two containers so callers can insert them at different z-levels
 * around the entity layer:
 *   tileLayer — tinted tile quads (goes UNDER the entity layer)
 *   hudLayer  — awareness bars + ghost/investigation glyphs (goes OVER it)
 *
 * The sim never imports this file. See CLAUDE.md architecture rule 4.
 */

import { Container, Sprite, Texture } from 'pixi.js';
import { CONFIG } from '../config';
import type { Guard, GuardState } from '../sim/guard';
import { isUnlit, lightLevelAt } from '../sim/light';
import type { MapData } from '../sim/map';
import type { FloorPatrol } from '../sim/patrol';
import type { SoundEvent } from '../sim/soundQueue';
import { floodSound } from '../sim/sound';
import type { GlyphAtlas } from './atlas';

const STATE_COLOR: Record<GuardState, number> = {
  unaware: 0x40a04a,
  suspicious: 0xd0a040,
  cautious: 0xd06040,
  alert: 0xff4040,
};

/** Cone-overlay blue for calm/unaware guards (the sound-style debug overlay). */
const CONE_BLUE = 0x4aa8ff;

export class PerceptionOverlay {
  readonly tileLayer: Container;
  readonly hudLayer: Container;
  /** Always-on focus reticle layer — visible regardless of debug toggle. */
  readonly focusLayer: Container;

  private readonly tileSize: number;
  private readonly atlas: GlyphAtlas;

  private readonly visionPool: Sprite[] = [];
  private readonly soundPool: Sprite[] = [];
  private readonly awarenessBg: Sprite[] = [];
  private readonly awarenessFg: Sprite[] = [];
  private readonly ghostPool: Sprite[] = [];
  private readonly investigationPool: Sprite[] = [];
  /** Section-outline edge quads (patrol debug): green covered / orange vacant. */
  private readonly sectionPool: Sprite[] = [];

  private focusReticle: Sprite | null = null;
  /** 'C' marker over the floor captain + a small mark at its roam target. */
  private captainMarker: Sprite | null = null;
  private captainTarget: Sprite | null = null;

  private _visible = false;

  constructor(atlas: GlyphAtlas, tileSize: number) {
    this.atlas = atlas;
    this.tileSize = tileSize;
    this.tileLayer = new Container();
    this.hudLayer = new Container();
    this.focusLayer = new Container();
    this.tileLayer.visible = false;
    this.hudLayer.visible = false;
  }

  /**
   * Highlight the focused guard. Always visible (not gated by the debug toggle)
   * so the player can see who they're locked on. The guard is a two-tile model
   * (legs tile + torso tile stacked one tile screen-up), so the reticle is a
   * screen-aligned box spanning BOTH tiles. `worldRotation` is the camera
   * rotation (this layer lives inside the rotated world container): the box
   * counter-rotates to stay screen-aligned, and the torso's screen-up offset is
   * derived the same rotation-aware way the renderer uses.
   */
  setFocus(focusedId: number | null, guards: readonly Guard[], worldRotation: number): void {
    if (focusedId === null) {
      if (this.focusReticle) this.focusReticle.visible = false;
      return;
    }
    const g = guards.find((gu) => gu.id === focusedId);
    if (!g || g.body.dead) {
      if (this.focusReticle) this.focusReticle.visible = false;
      return;
    }
    if (!this.focusReticle) {
      const s = new Sprite(Texture.WHITE);
      s.anchor.set(0.5, 0.5);
      s.tint = 0xffe060;
      s.alpha = 0.28;
      this.focusLayer.addChild(s);
      this.focusReticle = s;
    }
    // Torso screen-up offset (0 when crouched → the model collapses to one
    // tile). Matches the renderer's torso placement.
    const off = g.stance === 'crouch' ? 0 : CONFIG.render.playerTorsoOffsetPx;
    const cx = (g.visualX + 0.5) * this.tileSize;
    const cy = (g.visualY + 0.5) * this.tileSize;
    const tx = -off * Math.sin(worldRotation);
    const ty = -off * Math.cos(worldRotation);
    const s = this.focusReticle;
    s.visible = true;
    s.rotation = -worldRotation; // cancel the world container's rotation → screen-aligned
    s.x = cx + tx / 2; // centre between the legs tile and the torso tile
    s.y = cy + ty / 2;
    s.width = this.tileSize + 6;
    s.height = this.tileSize + off + 6; // span the legs tile + the torso tile
  }

  isVisible(): boolean {
    return this._visible;
  }

  setVisible(v: boolean): void {
    this._visible = v;
    this.tileLayer.visible = v;
    this.hudLayer.visible = v;
  }

  toggle(): void {
    this.setVisible(!this._visible);
  }

  update(guards: readonly Guard[], sounds: readonly SoundEvent[], map: MapData, floor?: FloorPatrol): void {
    if (!this._visible) return;

    // Hide all pooled sprites; the loops below will re-show what's active.
    for (const s of this.visionPool) s.visible = false;
    for (const s of this.soundPool) s.visible = false;
    for (const s of this.awarenessBg) s.visible = false;
    for (const s of this.awarenessFg) s.visible = false;
    for (const s of this.ghostPool) s.visible = false;
    for (const s of this.investigationPool) s.visible = false;
    for (const s of this.sectionPool) s.visible = false;

    // -------- vision cones — a clear translucent quad over every tile any
    //   (lit, live) guard can see, in the SAME overlay style as the sound flood
    //   below. Blue for calm guards, the warm state colour for agitated ones, so
    //   you read both WHERE guards look and WHO's alerted. Unioned per tile
    //   (max alpha, agitated colour wins) so overlapping cones don't stack into
    //   a dark blob; light modulates alpha so a tile too dark for the guard to
    //   see fades out. The always-on `visionField` (debug = every guard) reveals
    //   these tiles through the fog, so the quads read across the whole floor. --
    const cone = new Map<number, { alpha: number; color: number; agitated: boolean }>();
    for (const g of guards) {
      if (g.body.dead || !g.fovTiles) continue;
      if (isUnlit(map, g.x, g.y)) continue; // a guard in the dark isn't drawn
      const agitated = g.state !== 'unaware';
      const color = agitated ? STATE_COLOR[g.state] : CONE_BLUE;
      const baseA = agitated ? 0.42 : 0.32;
      for (const idx of g.fovTiles) {
        const tx = idx % map.width;
        const ty = Math.floor(idx / map.width);
        const lightMul = Math.min(1, lightLevelAt(map, tx, ty) / CONFIG.light.fullAt);
        if (lightMul <= 0) continue;
        const a = baseA * lightMul;
        const prev = cone.get(idx);
        if (!prev) {
          cone.set(idx, { alpha: a, color, agitated });
        } else {
          if (agitated && !prev.agitated) { prev.color = color; prev.agitated = true; }
          if (a > prev.alpha) prev.alpha = a;
        }
      }
    }
    let vi = 0;
    for (const [idx, c] of cone) {
      const s = this.getVision(vi++);
      s.tint = c.color;
      s.alpha = c.alpha;
      s.x = (idx % map.width) * this.tileSize;
      s.y = Math.floor(idx / map.width) * this.tileSize;
      s.visible = true;
    }
    for (let k = vi; k < this.visionPool.length; k++) this.visionPool[k]!.visible = false;

    // -------- sound flood --------
    let si = 0;
    for (const e of sounds) {
      // Re-flood per frame (cheap; loudness caps at ~25 so radius is small).
      const flood = floodSound(e.x, e.y, e.loudness, map);
      const fade = Math.max(0, 1 - e.age / CONFIG.sound.fadeAgeTicks);
      if (fade <= 0) continue;
      for (const [idx, residual] of flood) {
        const s = this.getSound(si++);
        const tx = idx % map.width;
        const ty = Math.floor(idx / map.width);
        s.tint = 0x60a0ff;
        s.alpha = Math.min(0.35, 0.06 + (residual / e.loudness) * 0.25 * fade);
        s.x = tx * this.tileSize;
        s.y = ty * this.tileSize;
        s.visible = true;
      }
    }

    // -------- awareness bars, ghost markers, investigation markers --------
    let bi = 0;
    let gi = 0;
    let ii = 0;
    for (const g of guards) {
      if (g.isJanitor) continue; // the janitor isn't a guard — no awareness readout
      // Bar above the guard: 1 tile wide, 3 px tall.
      const bx = g.visualX * this.tileSize;
      const by = g.visualY * this.tileSize - 5;
      const bg = this.getAwarenessBg(bi);
      const fg = this.getAwarenessFg(bi);
      bi++;
      const pct = Math.max(0, Math.min(1, g.awareness / CONFIG.awareness.max));
      bg.x = bx;
      bg.y = by;
      bg.tint = 0x1a1a1a;
      bg.alpha = 0.85;
      bg.setSize(this.tileSize, 3);
      bg.visible = true;
      fg.x = bx;
      fg.y = by;
      fg.tint = STATE_COLOR[g.state];
      fg.alpha = 0.95;
      fg.setSize(this.tileSize * pct, 3);
      fg.visible = true;

      // Ghost marker at last-known position when cautious+ and out of contact.
      if (g.lastKnownPlayerX !== null && g.lastKnownPlayerY !== null && (g.state === 'cautious' || g.state === 'alert')) {
        const seesNow =
          g.fovTiles?.has(g.lastKnownPlayerY * map.width + g.lastKnownPlayerX) === true;
        if (!seesNow) {
          const ghost = this.getGhost(gi++);
          ghost.x = g.lastKnownPlayerX * this.tileSize;
          ghost.y = g.lastKnownPlayerY * this.tileSize;
          ghost.visible = true;
        }
      }

      // Investigation marker (yellow ?) at investigation target when suspicious+.
      if (g.investigationX !== null && g.investigationY !== null && g.state !== 'unaware') {
        const inv = this.getInvestigation(ii++);
        inv.x = g.investigationX * this.tileSize;
        inv.y = g.investigationY * this.tileSize;
        inv.tint = STATE_COLOR[g.state];
        inv.visible = true;
      }
    }

    // -------- patrol sections + floor captain --------
    this.drawPatrol(guards, floor);
  }

  /** Outline each patrol section (green = a live guard owns it, orange = vacant)
   *  and mark the floor captain + its roam target. */
  private drawPatrol(guards: readonly Guard[], floor: FloorPatrol | undefined): void {
    let si = 0;
    if (floor) {
      const T = 2; // outline thickness (px)
      for (const rp of floor.rooms) {
        for (const s of rp.sections) {
          const covered = guards.some(
            (g) => !g.body.dead && !g.isCaptain && g.homeRoomId === s.roomId && g.sectionId === s.id,
          );
          const color = covered ? 0x40c060 : 0xe08030;
          const px = s.x0 * this.tileSize;
          const py = s.y0 * this.tileSize;
          const w = (s.x1 - s.x0 + 1) * this.tileSize;
          const h = (s.y1 - s.y0 + 1) * this.tileSize;
          const edges: ReadonlyArray<readonly [number, number, number, number]> = [
            [px, py, w, T],
            [px, py + h - T, w, T],
            [px, py, T, h],
            [px + w - T, py, T, h],
          ];
          for (const [ex, ey, ew, eh] of edges) {
            const sp = this.getSection(si++);
            sp.tint = color;
            sp.alpha = 0.5;
            sp.x = ex;
            sp.y = ey;
            sp.setSize(ew, eh);
            sp.visible = true;
          }
        }
      }
    }
    for (let k = si; k < this.sectionPool.length; k++) this.sectionPool[k]!.visible = false;

    // Captain marker ('C') + roam-target dot.
    const captain = floor && floor.captainId !== null
      ? guards.find((g) => g.id === floor.captainId && !g.body.dead)
      : undefined;
    if (!this.captainMarker) {
      const s = new Sprite(this.atlas.get('C') ?? Texture.EMPTY);
      s.scale.set(this.tileSize / this.atlas.cellH);
      s.tint = 0x38e0ff;
      s.visible = false;
      this.hudLayer.addChild(s);
      this.captainMarker = s;
    }
    if (!this.captainTarget) {
      const s = new Sprite(Texture.WHITE);
      s.setSize(this.tileSize, this.tileSize);
      s.tint = 0x38e0ff;
      s.alpha = 0.22;
      s.visible = false;
      this.tileLayer.addChild(s);
      this.captainTarget = s;
    }
    if (captain) {
      this.captainMarker.x = captain.visualX * this.tileSize;
      this.captainMarker.y = captain.visualY * this.tileSize - this.tileSize * 0.35;
      this.captainMarker.visible = true;
      if (captain.coverTargetX !== null && captain.coverTargetY !== null) {
        this.captainTarget.x = captain.coverTargetX * this.tileSize;
        this.captainTarget.y = captain.coverTargetY * this.tileSize;
        this.captainTarget.visible = true;
      } else {
        this.captainTarget.visible = false;
      }
    } else {
      this.captainMarker.visible = false;
      this.captainTarget.visible = false;
    }
  }

  private getSection(i: number): Sprite {
    while (i >= this.sectionPool.length) {
      const s = new Sprite(Texture.WHITE);
      s.visible = false;
      this.tileLayer.addChild(s);
      this.sectionPool.push(s);
    }
    return this.sectionPool[i]!;
  }

  private getVision(i: number): Sprite {
    while (i >= this.visionPool.length) {
      const s = new Sprite(Texture.WHITE);
      s.setSize(this.tileSize, this.tileSize);
      s.visible = false;
      this.tileLayer.addChild(s);
      this.visionPool.push(s);
    }
    return this.visionPool[i]!;
  }
  private getSound(i: number): Sprite {
    while (i >= this.soundPool.length) {
      const s = new Sprite(Texture.WHITE);
      s.setSize(this.tileSize, this.tileSize);
      s.visible = false;
      this.tileLayer.addChild(s);
      this.soundPool.push(s);
    }
    return this.soundPool[i]!;
  }
  private getAwarenessBg(i: number): Sprite {
    while (i >= this.awarenessBg.length) {
      const s = new Sprite(Texture.WHITE);
      s.visible = false;
      this.hudLayer.addChild(s);
      this.awarenessBg.push(s);
    }
    return this.awarenessBg[i]!;
  }
  private getAwarenessFg(i: number): Sprite {
    while (i >= this.awarenessFg.length) {
      const s = new Sprite(Texture.WHITE);
      s.visible = false;
      this.hudLayer.addChild(s);
      this.awarenessFg.push(s);
    }
    return this.awarenessFg[i]!;
  }
  private getGhost(i: number): Sprite {
    while (i >= this.ghostPool.length) {
      const tex = this.atlas.get('@') ?? Texture.EMPTY;
      const s = new Sprite(tex);
      const scale = this.tileSize / this.atlas.cellH;
      s.scale.set(scale);
      const glyphW = this.atlas.cellW * scale;
      s.pivot.set(0, 0);
      // Centre-offset roughly like the entity renderer does.
      const offsetX = (this.tileSize - glyphW) / 2;
      s.x = offsetX; // will be overridden per-frame
      s.tint = 0xa0a060;
      s.alpha = 0.55;
      this.hudLayer.addChild(s);
      this.ghostPool.push(s);
    }
    return this.ghostPool[i]!;
  }
  private getInvestigation(i: number): Sprite {
    while (i >= this.investigationPool.length) {
      const tex = this.atlas.get('?') ?? Texture.EMPTY;
      const s = new Sprite(tex);
      const scale = this.tileSize / this.atlas.cellH;
      s.scale.set(scale);
      s.alpha = 0.85;
      this.hudLayer.addChild(s);
      this.investigationPool.push(s);
    }
    return this.investigationPool[i]!;
  }
}
